import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Search, Filter, Plus, ChevronDown, X, Check, Eye, Loader2, Package, Palette, DollarSign, Zap, ShoppingBag, Upload, Sparkles, Image as ImageIcon
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const Products = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [internalItems, setInternalItems] = useState([]); // Stares Group Internal Catalog (custom products)
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(null);
  
  // Style Lookup state
  const [styleLookup, setStyleLookup] = useState('');
  const [styleLookupLoading, setStyleLookupLoading] = useState(false);
  const [styleLookupResult, setStyleLookupResult] = useState(null);

  // Image override modal (upload custom photo for any product row → saves into Internal Catalog)
  const [imageOverrideTarget, setImageOverrideTarget] = useState(null); // the product object
  const [overrideForm, setOverrideForm] = useState({ color_name: '', hex: '#000000', view: 'front', image_base64: '' });
  const [savingOverride, setSavingOverride] = useState(false);
  const overrideFileRef = useRef(null);

  // Create-from-scratch custom product modal
  const [showCreateCustom, setShowCreateCustom] = useState(false);
  const [creatingCustom, setCreatingCustom] = useState(false);
  const [customForm, setCustomForm] = useState({
    style_code: '', brand: '', style_name: '', description: '',
    sizes: [
      { size: 'S', price: 0 }, { size: 'M', price: 0 }, { size: 'L', price: 0 },
      { size: 'XL', price: 0 }, { size: '2XL', price: 0 },
    ],
  });

  const fetchInternalCatalog = async () => {
    try {
      const res = await axios.get('/internal-catalog');
      setInternalItems(res.data || []);
    } catch (e) {
      // Silently — endpoint requires admin; non-admins just don't see custom items
      setInternalItems([]);
    }
  };

  const fetchProducts = async () => {
    try {
      let url = '/products';
      const params = new URLSearchParams();
      if (selectedBrand) params.append('brand_id', selectedBrand);
      if (selectedCategory) params.append('category_id', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;
      
      console.log('Fetching products from:', url);
      const response = await axios.get(url, { timeout: 10000 });
      console.log('Products response:', response.data?.length || 0);
      setProducts(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load products:', error?.message || error);
      setProducts([]);
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get('/products/brands');
      setBrands(response.data || []);
    } catch (error) {
      console.error('Failed to load brands');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/products/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchBrands();
    fetchCategories();
    fetchInternalCatalog();
  }, []);

  useEffect(() => {
    if (searchTerm || selectedBrand || selectedCategory) {
      const debounce = setTimeout(() => {
        fetchProducts();
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [searchTerm, selectedBrand, selectedCategory]);

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const addToProposal = () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    
    // Store selected products in sessionStorage for the quote builder
    const selectedProductData = products.filter(p => selectedProducts.includes(p.styleID));
    sessionStorage.setItem('selectedProducts', JSON.stringify(selectedProductData));
    
    toast.success(`${selectedProducts.length} product(s) ready to add to proposal`);
    navigate('/quote');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBrand('');
    setSelectedCategory('');
  };

  // Style Lookup - Search for specific style codes across ALL vendors
  const handleStyleLookup = async () => {
    if (!styleLookup.trim()) {
      toast.error('Please enter a style code');
      return;
    }
    
    setStyleLookupLoading(true);
    setStyleLookupResult(null);
    
    try {
      // Search all vendors at once
      const response = await axios.get(`/products/search/all/${encodeURIComponent(styleLookup.trim())}`);
      const results = response.data;
      const searchTerm = styleLookup.trim().toUpperCase();
      
      if (results.length === 1) {
        setStyleLookupResult(results[0]);
        toast.success(`Found: ${results[0].brandName} ${results[0].styleName} from ${results[0].supplier}`);
      } else if (results.length > 1) {
        // Multiple results - prioritize:
        // 1. Exact style ID match
        // 2. If no exact match, prefer the one with most colors
        
        const exactMatch = results.find(r => 
          r.styleID?.toUpperCase() === searchTerm || 
          r.styleName?.toUpperCase() === searchTerm
        );
        
        if (exactMatch) {
          setStyleLookupResult(exactMatch);
          toast.success(`Found exact match: ${exactMatch.brandName} ${exactMatch.styleID} from ${exactMatch.supplier}`);
        } else {
          // No exact match - show the one with most colors (most popular)
          const best = results.reduce((a, b) => 
            (a.colors?.length || 0) > (b.colors?.length || 0) ? a : b
          );
          setStyleLookupResult(best);
          toast.success(`Found ${results.length} matches - showing ${best.supplier}: ${best.brandName} ${best.styleName}`);
        }
      }
    } catch (error) {
      // Fallback to S&S only search
      try {
        const response = await axios.get(`/products/search/style/${encodeURIComponent(styleLookup.trim())}`);
        setStyleLookupResult(response.data);
        response.data.supplier = 'S&S Activewear';
        toast.success(`Found: ${response.data.brandName} ${response.data.styleName}`);
      } catch (fallbackError) {
        if (fallbackError.response?.status === 404) {
          toast.error(`Style "${styleLookup}" not found in any vendor catalog`);
        } else {
          toast.error('Failed to search style');
        }
        setStyleLookupResult(null);
      }
    } finally {
      setStyleLookupLoading(false);
    }
  };

  const addStyleToProposal = () => {
    if (!styleLookupResult) return;
    
    const productData = [{
      ...styleLookupResult,
      quantity: 24,
      selectedColor: styleLookupResult.colors?.[0]?.colorName || 'Black',
      sizes: { S: 0, M: 0, L: 0, XL: 0, '2XL': 0, '3XL': 0 }
    }];
    
    sessionStorage.setItem('selectedProducts', JSON.stringify(productData));
    toast.success('Product added to proposal');
    navigate('/quote');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Map an InternalCatalogItem into the same shape the products table renders.
  const internalToProduct = (item) => {
    const firstColor = (item.colors || [])[0] || {};
    const firstViews = firstColor.views || {};
    const frontImg = firstViews.front || firstColor.image_base64 || null;
    return {
      styleID: item.style_code,
      styleName: item.style_name,
      brandName: item.brand,
      styleImage: frontImg,
      basePrice: 0, // Unused for internal rows — pricing is rendered from _sizes.
      _internalId: item.id,
      _isInternal: true,
      _sizes: item.sizes || [],
      _createdAt: item.created_at || '',
      _internalColors: item.colors || [],
      colors: (item.colors || []).map((c) => ({
        colorName: c.color_name,
        hex: c.hex || '#cccccc',
        image: (c.views && c.views.front) || c.image_base64,
      })),
    };
  };

  // The list rendered in the table.
  //   • Internal items with PRICING (sizes set) take over the vendor product (purple [CUSTOM] badge).
  //   • Internal items WITHOUT pricing only ENRICH the matching vendor row with view photos
  //     (vendor row stays, gets a "📸 N/4 views" badge — no duplicate row).
  //   • Internal items with no vendor counterpart show as standalone custom rows.
  const mergedProducts = (() => {
    const internalAsProducts = internalItems.map(internalToProduct);
    internalAsProducts.sort((a, b) => (b._createdAt || '').localeCompare(a._createdAt || ''));

    const pricedInternal = internalAsProducts.filter((p) => p._sizes.length > 0);
    const enrichmentOnly = internalAsProducts.filter((p) => p._sizes.length === 0);
    const enrichmentByCode = new Map(enrichmentOnly.map((p) => [String(p.styleID || '').toLowerCase(), p]));

    // Vendor rows: drop ones replaced by priced internal entries; enrich the rest in place.
    const pricedCodes = new Set(pricedInternal.map((p) => (p.styleID || '').toLowerCase()));
    const vendorEnriched = products
      .filter((p) => !pricedCodes.has(String(p.styleID || '').toLowerCase()))
      .map((vendor) => {
        const enrich = enrichmentByCode.get(String(vendor.styleID || '').toLowerCase());
        if (!enrich) return vendor;
        // Borrow internal colors/views for the Mockup Designer and use front photo for the table.
        return {
          ...vendor,
          styleImage: enrich.styleImage || vendor.styleImage,
          _enrichedFromInternalId: enrich._internalId,
          _internalColors: enrich._internalColors,
          _viewCount: (() => {
            const c = (enrich._internalColors || [])[0];
            const v = c?.views || {};
            return ['front', 'back', 'left_sleeve', 'right_sleeve'].filter((k) => v[k]).length
              || (c?.image_base64 ? 1 : 0);
          })(),
        };
      });

    // Standalone enrichment-only internal items (no matching vendor) — show as their own rows.
    const vendorCodes = new Set(products.map((p) => String(p.styleID || '').toLowerCase()));
    const orphanEnrichment = enrichmentOnly.filter((p) => !vendorCodes.has(String(p.styleID || '').toLowerCase()));

    const term = searchTerm.toLowerCase();
    const matchTerm = (p) => !term
      || (p.styleID || '').toLowerCase().includes(term)
      || (p.styleName || '').toLowerCase().includes(term)
      || (p.brandName || '').toLowerCase().includes(term);

    return [
      ...pricedInternal.filter(matchTerm),
      ...orphanEnrichment.filter(matchTerm),
      ...vendorEnriched.filter(matchTerm),
    ];
  })();

  const openImageOverride = (product) => {
    setImageOverrideTarget(product);
    const existingFirstColor = product._internalColors?.[0];
    setOverrideForm({
      color_name: existingFirstColor?.color_name || product.colors?.[0]?.colorName || 'Default',
      hex: existingFirstColor?.hex || product.colors?.[0]?.hex || '#000000',
      view: 'front',
      image_base64: '',
    });
  };

  const handleOverrideFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error('Image too large (max 4 MB)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setOverrideForm((c) => ({ ...c, image_base64: ev.target.result }));
    reader.readAsDataURL(f);
  };

  const saveImageOverride = async () => {
    if (!imageOverrideTarget) return;
    if (!overrideForm.color_name.trim()) { toast.error('Color name is required'); return; }
    if (!overrideForm.image_base64) { toast.error('Upload a photo first'); return; }
    setSavingOverride(true);
    try {
      const styleCode = imageOverrideTarget.styleID;
      // 1) Find or create the style in Internal Catalog (no pricing → enrichment-only)
      let itemId = imageOverrideTarget._internalId || imageOverrideTarget._enrichedFromInternalId;
      if (!itemId) {
        try {
          const found = await axios.get(`/internal-catalog/search/${encodeURIComponent(styleCode)}`);
          itemId = found.data?.id;
        } catch (err) {
          if (err.response?.status !== 404) throw err;
        }
      }
      if (!itemId) {
        const created = await axios.post('/internal-catalog', {
          style_code: styleCode,
          brand: imageOverrideTarget.brandName || 'Custom',
          style_name: imageOverrideTarget.styleName || styleCode,
        });
        itemId = created.data?.id;
      }
      // 2) Upload color with the selected view. Backend MERGES with existing views,
      //    so uploading "back" doesn't wipe out a previously-saved "front" photo.
      await axios.post(`/internal-catalog/${itemId}/colors`, {
        color_name: overrideForm.color_name.trim(),
        hex: overrideForm.hex || null,
        views: { [overrideForm.view]: overrideForm.image_base64 },
      });
      toast.success(`Saved ${overrideForm.view.replace('_', ' ')} view for ${styleCode} · ${overrideForm.color_name}`);
      setImageOverrideTarget(null);
      await fetchInternalCatalog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save custom image');
    } finally {
      setSavingOverride(false);
    }
  };

  // ===== Create-from-scratch custom product =====
  const updateCustomSize = (idx, field, value) => {
    setCustomForm((f) => ({
      ...f,
      sizes: f.sizes.map((s, i) => (i === idx ? { ...s, [field]: field === 'price' ? Number(value) || 0 : value } : s)),
    }));
  };
  const addCustomSizeRow = () => setCustomForm((f) => ({ ...f, sizes: [...f.sizes, { size: '', price: 0 }] }));
  const removeCustomSizeRow = (idx) => setCustomForm((f) => ({ ...f, sizes: f.sizes.filter((_, i) => i !== idx) }));

  const submitCustomProduct = async () => {
    const f = customForm;
    if (!f.style_code.trim() || !f.brand.trim() || !f.style_name.trim()) {
      toast.error('Style code, brand, and name are required');
      return;
    }
    const cleanSizes = f.sizes
      .filter((s) => s.size.trim() && Number(s.price) > 0)
      .map((s) => ({ size: s.size.trim(), price: Number(s.price) || 0 }));
    setCreatingCustom(true);
    try {
      await axios.post('/internal-catalog', {
        style_code: f.style_code.trim(),
        brand: f.brand.trim(),
        style_name: f.style_name.trim(),
        description: f.description || null,
        sizes: cleanSizes,
      });
      toast.success(`Created custom product ${f.brand} ${f.style_code}`);
      setShowCreateCustom(false);
      setCustomForm({
        style_code: '', brand: '', style_name: '', description: '',
        sizes: [
          { size: 'S', price: 0 }, { size: 'M', price: 0 }, { size: 'L', price: 0 },
          { size: 'XL', price: 0 }, { size: '2XL', price: 0 },
        ],
      });
      await fetchInternalCatalog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create product');
    } finally {
      setCreatingCustom(false);
    }
  };

  const ProductModal = ({ product, onClose }) => (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>{product.styleName}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '24px', marginBottom: '20px' }}>
          <img 
            src={product.styleImage || `https://via.placeholder.com/200x200?text=${product.styleID}`}
            alt={product.styleName}
            style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '8px' }}
          />
          <div>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>{product.brandName}</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: '#e74c3c', margin: '0 0 16px 0' }}>
              {formatCurrency(product.basePrice * 2.08)} <span style={{ fontSize: '14px', color: '#9ca3af', fontWeight: '400' }}>per unit</span>
            </p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>Base Cost: {formatCurrency(product.basePrice)}</p>
            <p style={{ fontSize: '13px', color: '#374151', margin: '0 0 16px 0' }}>{product.description}</p>
            
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Available Sizes:</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {(product.sizes || ['S', 'M', 'L', 'XL', '2XL', '3XL']).map(size => (
                  <span key={size} style={{ 
                    padding: '4px 12px', 
                    background: '#f3f4f6', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {size}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
            Available Colors ({product.colorCount || product.colors?.length || 0}):
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {(product.colors || []).slice(0, 12).map((color, i) => (
              <div 
                key={i}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px',
                  background: color.hex || '#ccc',
                  border: '2px solid #e5e7eb',
                  cursor: 'pointer'
                }}
                title={color.colorName}
              />
            ))}
            {(product.colors?.length || 0) > 12 && (
              <span style={{ 
                padding: '8px 12px', 
                background: '#f3f4f6', 
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                +{product.colors.length - 12} more
              </span>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button 
            onClick={() => {
              toggleProductSelection(product.styleID);
              onClose();
            }}
            style={{ background: '#e74c3c' }}
          >
            {selectedProducts.includes(product.styleID) ? 'Remove from Selection' : 'Add to Selection'}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard" data-testid="products-page">
      {/* Header */}
      <AppHeader />

      <main className="dashboard-content">
        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 className="dashboard-title" style={{ marginBottom: '4px' }}>Manage Products</h1>
            <p style={{ color: '#6b7280', margin: 0 }}>{mergedProducts.length} products available</p>
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => setShowCreateCustom(true)}
              data-testid="create-custom-product-btn"
              style={{ background: '#7c3aed', color: '#fff' }}>
              <Plus size={16} style={{ marginRight: 4 }} /> Create Custom Product
            </Button>
            {selectedProducts.length > 0 && (
              <Button onClick={addToProposal} style={{ background: '#e74c3c' }}>
                <Plus size={16} /> Add {selectedProducts.length} to Proposal
              </Button>
            )}
          </div>
        </div>

        {/* Style Lookup - Quick Search */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', 
          padding: '24px', 
          borderRadius: '12px', 
          marginBottom: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Zap size={20} style={{ color: '#e74c3c' }} />
            <span style={{ fontWeight: '700', color: 'white', fontSize: '16px' }}>Quick Style Lookup</span>
            <span style={{ color: '#9ca3af', fontSize: '13px', marginLeft: '8px' }}>
              Search S&S Activewear + SanMar (e.g., 3600, G500, PC61, ST350)
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
            <div style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
              <Search size={18} style={{ 
                position: 'absolute', 
                left: '14px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} />
              <Input
                placeholder="Enter style code..."
                value={styleLookup}
                onChange={(e) => setStyleLookup(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleStyleLookup()}
                style={{ 
                  paddingLeft: '44px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  background: 'white'
                }}
                data-testid="style-lookup-input"
              />
            </div>
            <Button
              onClick={handleStyleLookup}
              disabled={styleLookupLoading}
              style={{
                height: '48px',
                padding: '0 24px',
                background: '#e74c3c',
                fontSize: '15px',
                fontWeight: '600'
              }}
              data-testid="style-lookup-button"
            >
              {styleLookupLoading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <>
                  <Search size={18} style={{ marginRight: '8px' }} />
                  Find Style
                </>
              )}
            </Button>
          </div>

          {/* Style Lookup Result */}
          {styleLookupResult && (
            <div style={{
              marginTop: '20px',
              background: 'white',
              borderRadius: '10px',
              padding: '20px',
              animation: 'fadeIn 0.3s ease'
            }}>
              <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
              
              <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                {/* Product Image */}
                <div style={{
                  width: '120px',
                  height: '120px',
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  flexShrink: 0
                }}>
                  {styleLookupResult.styleImage ? (
                    <img 
                      src={styleLookupResult.styleImage} 
                      alt={styleLookupResult.styleName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Package size={40} color="#9ca3af" />
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <p style={{ 
                          fontSize: '12px', 
                          color: '#e74c3c', 
                          fontWeight: '600',
                          margin: 0,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {styleLookupResult.brandName}
                        </p>
                        {styleLookupResult.supplier && (
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 8px',
                            background: styleLookupResult.supplier === 'SanMar' ? '#dcfce7' : '#dbeafe',
                            color: styleLookupResult.supplier === 'SanMar' ? '#166534' : '#1e40af',
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            {styleLookupResult.supplier}
                          </span>
                        )}
                      </div>
                      <h3 style={{ 
                        fontSize: '20px', 
                        fontWeight: '700', 
                        color: '#1a1a2e',
                        margin: '0 0 4px'
                      }}>
                        {styleLookupResult.styleName}
                      </h3>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                        Style #{styleLookupResult.styleID}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '24px', fontWeight: '700', color: '#e74c3c', margin: 0 }}>
                        {formatCurrency(styleLookupResult.basePrice * 2.08)}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0' }}>
                        Cost: {formatCurrency(styleLookupResult.basePrice)}
                      </p>
                    </div>
                  </div>

                  {/* Colors Preview */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Palette size={16} color="#6b7280" />
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>
                        {styleLookupResult.colors?.length || 0} colors available
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {styleLookupResult.colors?.slice(0, 8).map((color, i) => (
                        <div
                          key={i}
                          title={color.colorName}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            background: color.hex || '#ccc',
                            border: '2px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }}
                        />
                      ))}
                      {(styleLookupResult.colors?.length || 0) > 8 && (
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          background: '#f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          fontWeight: '600',
                          color: '#6b7280'
                        }}>
                          +{styleLookupResult.colors.length - 8}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sizes */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {(styleLookupResult.sizes || ['S', 'M', 'L', 'XL', '2XL', '3XL']).map(size => (
                      <span 
                        key={size}
                        style={{
                          padding: '4px 10px',
                          background: '#f3f4f6',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#374151'
                        }}
                      >
                        {size}
                      </span>
                    ))}
                  </div>

                  {/* Action Button */}
                  <Button
                    onClick={addStyleToProposal}
                    style={{
                      background: '#e74c3c',
                      fontWeight: '600'
                    }}
                    data-testid="add-style-to-proposal"
                  >
                    <Plus size={16} style={{ marginRight: '6px' }} />
                    Add to Proposal
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div style={{ 
          background: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Filter size={18} style={{ color: '#6b7280' }} />
            <span style={{ fontWeight: '600', color: '#374151' }}>Filter Results</span>
          </div>
          
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* Brand Filter */}
            <div style={{ minWidth: '200px' }}>
              <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Brand</label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  borderRadius: '6px', 
                  border: '1px solid #e5e7eb',
                  fontSize: '14px'
                }}
              >
                <option value="">All Brands</option>
                {brands.map(brand => (
                  <option key={brand.brandID || brand.brandId} value={brand.brandID || brand.brandId}>{brand.name || brand.brandName}</option>
                ))}
              </select>
            </div>
            
            {/* Category Filter */}
            <div style={{ minWidth: '200px' }}>
              <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  borderRadius: '6px', 
                  border: '1px solid #e5e7eb',
                  fontSize: '14px'
                }}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.categoryID || cat.categoryId} value={cat.categoryID || cat.categoryId}>{cat.name || cat.categoryName}</option>
                ))}
              </select>
            </div>
            
            {/* Search */}
            <div style={{ flex: 1, minWidth: '250px' }}>
              <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Search Products</label>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <Input
                  placeholder="Search by style, brand, name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '36px' }}
                />
              </div>
            </div>
            
            {/* Clear Filters */}
            {(searchTerm || selectedBrand || selectedCategory) && (
              <Button variant="outline" onClick={clearFilters}>
                <X size={14} /> Clear All Filters
              </Button>
            )}
          </div>
          
          {/* Your Selection */}
          {selectedProducts.length > 0 && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#fef2f2', borderRadius: '6px' }}>
              <span style={{ fontWeight: '600', color: '#e74c3c' }}>
                YOUR SELECTION: {selectedProducts.length} product(s) selected
              </span>
            </div>
          )}
        </div>

        {/* Products Table */}
        <div style={{ 
          background: 'white', 
          borderRadius: '8px', 
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>Loading products...</div>
          ) : mergedProducts.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
              <ShoppingBag size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <p>No products found.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', width: '40px' }}>
                    <input 
                      type="checkbox" 
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProducts(mergedProducts.map(p => p.styleID));
                        } else {
                          setSelectedProducts([]);
                        }
                      }}
                      checked={selectedProducts.length === mergedProducts.length && mergedProducts.length > 0}
                      style={{ width: '16px', height: '16px', accentColor: '#e74c3c' }}
                    />
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Style #</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Product Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Image</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Price</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Colors</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mergedProducts.map((product) => (
                  <tr 
                    key={(product._isInternal ? 'i_' : 'v_') + product.styleID} 
                    style={{ 
                      borderBottom: '1px solid #e5e7eb',
                      background: selectedProducts.includes(product.styleID) ? '#fef2f2' : 'white'
                    }}
                    data-testid={`product-row-${product.styleID}`}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedProducts.includes(product.styleID)}
                        onChange={() => toggleProductSelection(product.styleID)}
                        style={{ width: '16px', height: '16px', accentColor: '#e74c3c' }}
                      />
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <a 
                        href="#" 
                        onClick={(e) => { e.preventDefault(); setShowProductModal(product); }}
                        style={{ color: '#e74c3c', textDecoration: 'none', fontWeight: '600' }}
                      >
                        {product.styleID}
                      </a>
                      {product._isInternal && (
                        <span style={{ marginLeft: 6, fontSize: 9, padding: '2px 6px', background: '#ede9fe', color: '#6d28d9', borderRadius: 4, fontWeight: 700, letterSpacing: 0.5 }}>
                          CUSTOM
                        </span>
                      )}
                      {!product._isInternal && product._viewCount > 0 && (
                        <span style={{ marginLeft: 6, fontSize: 9, padding: '2px 6px', background: '#d1fae5', color: '#065f46', borderRadius: 4, fontWeight: 700, letterSpacing: 0.5 }}
                          title="Custom view photos uploaded for this style">
                          📸 {product._viewCount}/4 VIEWS
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div>
                        <p style={{ margin: 0, fontWeight: '600', color: '#111827' }}>{product.styleName}</p>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6b7280' }}>{product.brandName}</p>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {product.styleImage ? (
                        <img 
                          src={product.styleImage} 
                          alt={product.styleName}
                          style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', background: '#f3f4f6' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{ width: 60, height: 60, background: '#f3f4f6', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <ImageIcon size={20} color="#9ca3af" />
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {product._isInternal ? (
                        <>
                          <p style={{ margin: 0, fontWeight: '700', color: '#7c3aed' }}>
                            {product._sizes.length ? formatCurrency(Math.min(...product._sizes.map(s => Number(s.price) || 0))) : <span style={{ color: '#dc2626' }}>No pricing</span>}
                            {product._sizes.length > 1 && ` – ${formatCurrency(Math.max(...product._sizes.map(s => Number(s.price) || 0)))}`}
                          </p>
                          <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>Your pricing</p>
                        </>
                      ) : (
                        <>
                          <p style={{ margin: 0, fontWeight: '700', color: '#e74c3c' }}>{formatCurrency(product.basePrice * 2.08)}</p>
                          <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>Cost: {formatCurrency(product.basePrice)}</p>
                        </>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', maxWidth: '150px' }}>
                        {(product.colors || []).slice(0, 6).map((color, i) => (
                          <div 
                            key={i}
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '3px',
                              background: color.hex || '#ccc',
                              border: '1px solid #e5e7eb'
                            }}
                            title={color.colorName}
                          />
                        ))}
                        {(product.colors?.length || 0) > 6 && (
                          <span style={{ fontSize: '11px', color: '#6b7280', alignSelf: 'center' }}>
                            +{product.colors.length - 6}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setShowProductModal(product)}
                          style={{ color: '#6b7280' }}
                          data-testid={`view-${product.styleID}`}
                        >
                          <Eye size={16} />
                        </Button>
                        {product._isInternal ? (
                          <Button variant="ghost" size="sm"
                            onClick={() => navigate('/internal-catalog')}
                            style={{ color: '#7c3aed' }}
                            data-testid={`manage-${product.styleID}`}
                            title="Manage in Internal Catalog">
                            <Sparkles size={16} />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm"
                            onClick={() => openImageOverride(product)}
                            style={{ color: '#7c3aed' }}
                            data-testid={`override-${product.styleID}`}
                            title="Upload your own photo">
                            <Upload size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Product Detail Modal */}
      {showProductModal && (
        <ProductModal product={showProductModal} onClose={() => setShowProductModal(null)} />
      )}

      {/* Image-override modal (vendor product → upload your own photo to Internal Catalog) */}
      <Dialog open={!!imageOverrideTarget} onOpenChange={(o) => !o && setImageOverrideTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Custom Photo · {imageOverrideTarget?.styleID}</DialogTitle>
            <DialogDescription>
              Upload a real photo for any of the 4 views — Front, Back, Left Sleeve, Right Sleeve. Saved into your Internal Catalog and reused by the Mockup Designer (each view auto-loads on the matching canvas side).
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 0' }}>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Color name *</Label>
                <Input value={overrideForm.color_name}
                  onChange={(e) => setOverrideForm((c) => ({ ...c, color_name: e.target.value }))}
                  placeholder="e.g. Heather Gray"
                  data-testid="override-color-name" />
              </div>
              <div>
                <Label className="text-xs">Hex</Label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="color" value={overrideForm.hex || '#000000'}
                    onChange={(e) => setOverrideForm((c) => ({ ...c, hex: e.target.value }))}
                    style={{ width: 36, height: 36, padding: 0, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                  <Input value={overrideForm.hex || ''}
                    onChange={(e) => setOverrideForm((c) => ({ ...c, hex: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs">View *</Label>
                <select value={overrideForm.view}
                  onChange={(e) => setOverrideForm((c) => ({ ...c, view: e.target.value }))}
                  data-testid="override-view-select"
                  style={{ width: '100%', height: 40, borderRadius: 6, border: '1px solid #cbd5e1', padding: '0 10px', fontSize: 14 }}>
                  <option value="front">Front</option>
                  <option value="back">Back</option>
                  <option value="left_sleeve">Left Sleeve</option>
                  <option value="right_sleeve">Right Sleeve</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Product photo *</Label>
              <input ref={overrideFileRef} type="file" accept="image/*" onChange={handleOverrideFile}
                style={{ display: 'none' }} data-testid="override-image-input" />
              {overrideForm.image_base64 ? (
                <div style={{ marginTop: 6 }}>
                  <img src={overrideForm.image_base64} alt="preview"
                    style={{ width: '100%', maxHeight: 220, objectFit: 'contain', background: '#f8fafc', borderRadius: 8 }} />
                  <Button size="sm" variant="outline" onClick={() => setOverrideForm((c) => ({ ...c, image_base64: '' }))}
                    style={{ marginTop: 8 }}>
                    <X size={14} style={{ marginRight: 4 }} /> Replace
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={() => overrideFileRef.current?.click()}
                  style={{ width: '100%', marginTop: 6 }}>
                  <Upload size={14} style={{ marginRight: 6 }} /> Choose image (max 4 MB)
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImageOverrideTarget(null)} disabled={savingOverride}>Cancel</Button>
            <Button onClick={saveImageOverride} disabled={savingOverride}
              style={{ background: '#7c3aed', color: '#fff' }}
              data-testid="save-override-btn">
              {savingOverride ? <><Loader2 className="animate-spin" size={14} style={{ marginRight: 6 }} />Saving…</> : <>Save Custom Photo</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Custom Product modal */}
      <Dialog open={showCreateCustom} onOpenChange={(o) => !o && setShowCreateCustom(false)}>
        <DialogContent style={{ maxWidth: 560 }}>
          <DialogHeader>
            <DialogTitle>Create Custom Product</DialogTitle>
            <DialogDescription>
              Build your own product with your own pricing per size. You can add color photos right after saving.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 0', maxHeight: 480, overflowY: 'auto' }}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Style code *</Label>
                <Input value={customForm.style_code}
                  placeholder="e.g. SG-TEE-001"
                  onChange={(e) => setCustomForm((f) => ({ ...f, style_code: e.target.value }))}
                  data-testid="custom-style-code" />
              </div>
              <div>
                <Label className="text-xs">Brand *</Label>
                <Input value={customForm.brand}
                  placeholder="e.g. Stares Group"
                  onChange={(e) => setCustomForm((f) => ({ ...f, brand: e.target.value }))}
                  data-testid="custom-brand" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Product name *</Label>
              <Input value={customForm.style_name}
                placeholder="e.g. Heavy Cotton Hoodie"
                onChange={(e) => setCustomForm((f) => ({ ...f, style_name: e.target.value }))}
                data-testid="custom-style-name" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={customForm.description}
                placeholder="Fabric, weight, fit notes…"
                onChange={(e) => setCustomForm((f) => ({ ...f, description: e.target.value }))} />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <Label className="text-xs">Pricing per size</Label>
                <Button size="sm" variant="outline" onClick={addCustomSizeRow}>
                  <Plus size={12} style={{ marginRight: 4 }} /> Add size
                </Button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {customForm.sizes.map((row, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 32px', gap: 8, alignItems: 'center' }}>
                    <Input value={row.size} placeholder="Size"
                      onChange={(e) => updateCustomSize(i, 'size', e.target.value)}
                      style={{ fontWeight: 600 }}
                      data-testid={`custom-size-${i}`} />
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>$</span>
                      <Input type="number" step="0.01" min="0" value={row.price}
                        onChange={(e) => updateCustomSize(i, 'price', e.target.value)}
                        style={{ paddingLeft: 22 }}
                        data-testid={`custom-price-${i}`} />
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => removeCustomSizeRow(i)}
                      style={{ color: '#dc2626' }}>
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCustom(false)} disabled={creatingCustom}>Cancel</Button>
            <Button onClick={submitCustomProduct} disabled={creatingCustom}
              style={{ background: '#7c3aed', color: '#fff' }}
              data-testid="save-custom-product-btn">
              {creatingCustom ? <><Loader2 className="animate-spin" size={14} style={{ marginRight: 6 }} />Creating…</> : <>Create Product</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
